//@flow
import { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import getter from 'dibs-object-getter';

import { Button } from 'dibs-elements/exports/Button';

import { firmPortfolio as firmPortfolioMutation } from '../../mutations/firmPortfolio';
import { logError } from '../../../helpers';

import styles from './FolderMenuOption.scss';

import type { RelayProp } from 'react-relay';
import type { FolderMenuOption_portfolio as Portfolio } from './__generated__/FolderMenuOption_portfolio.graphql';

const selectMessage = (
    <FormattedMessage
        id="abt.designProfile.FolderMenuOption.selectFolderMessage"
        defaultMessage="Select"
    />
);

const notPublicMessage = (
    <FormattedMessage
        id="abt.designProfile.FolderMenuOption.notPublic"
        defaultMessage="Not Public"
    />
);

const noItemsMessage = (
    <FormattedMessage
        id="abt.designProfile.FolderMenuOption.noItemsMessage"
        defaultMessage="No Items"
    />
);

type Props = {
    closeModal?: Function,
    firmId: ?string,
    portfolio?: ?Portfolio,
    onFolderSelection: Function,
    relay?: RelayProp,
};

export class FolderMenuOptionComponent extends Component<Props> {
    constructor(props: Props) {
        super(props);
    }

    selectFolder = () => {
        const { closeModal, firmId, portfolio, onFolderSelection, relay } = this.props;

        const portfolioId = getter(portfolio).value('serviceId');

        if (!relay) {
            logError('Did not receive relay prop; cannot set firm portfolio');
            return;
        }

        if (!firmId || !portfolioId) {
            logError(
                `Invalid values for firmId, portfolioId, got ${String(firmId)} and ${String(
                    portfolioId
                )}, respectively`
            );
            return;
        }

        firmPortfolioMutation(relay.environment, { firmId, portfolioId }).then(result => {
            if (!result.hasError) {
                onFolderSelection();
            } else {
                const errors = result.errors || [];
                errors.forEach(e => logError(e));
                if (closeModal) {
                    closeModal();
                }
            }
        });
    };

    render() {
        const { portfolio } = this.props;

        if (!portfolio) {
            return null;
        }

        const { serviceId, firstItemImage, isPublic, name } = portfolio;

        const itemCount = getter(portfolio).at('items').value('totalResults') || 0;
        const hasItems = itemCount > 0;
        const canSelectFolder = isPublic && itemCount > 0;

        let buttonCopy = selectMessage;
        if (!isPublic) {
            buttonCopy = notPublicMessage;
        } else if (!hasItems) {
            buttonCopy = noItemsMessage;
        }

        const imageStyle = { backgroundImage: `url(${firstItemImage || ''})` };

        return (
            <div className={styles.container}>
                <div className={styles.imageContainer} style={imageStyle} />
                <div className={styles.folderName}>{name}</div>
                <Button
                    className={styles.selectButton}
                    dataTn={`select-folder-${serviceId || ''}`}
                    disabled={!canSelectFolder}
                    onClick={(e: SyntheticEvent<*>) => {
                        e.preventDefault();
                        this.selectFolder();
                    }}
                    size="small"
                >
                    {buttonCopy}
                </Button>
            </div>
        );
    }
}

export const FolderMenuOption = createFragmentContainer(FolderMenuOptionComponent, {
    portfolio: graphql`
        fragment FolderMenuOption_portfolio on PortfolioV2 {
            serviceId
            firstItemImage(size: large)
            isPublic
            items {
                totalResults
            }
            name
        }
    `,
});
